import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup,
  CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { api } from "@/lib/api";
import {
  Target, UserRound, Sparkles, ListChecks,
  GraduationCap, Plane, Wallet, MonitorSmartphone,
  Users, HeartHandshake, Building2,
  FileText, Receipt, LayoutDashboard, Zap, IndianRupee,
} from "lucide-react";

const KIND_META = {
  lead:        { icon: Target,           label: "Leads" },
  contact:     { icon: UserRound,        label: "Contacts" },
  opportunity: { icon: Sparkles,         label: "Opportunities" },
  task:        { icon: ListChecks,       label: "Tasks" },
  program:     { icon: GraduationCap,    label: "Academy" },
  enrolment:   { icon: GraduationCap,    label: "Academy" },
  application: { icon: Plane,            label: "Applications" },
  wealth:      { icon: Wallet,           label: "Wealth" },
  project:     { icon: MonitorSmartphone,label: "Digital" },
  employee:    { icon: Users,            label: "HR" },
  candidate:   { icon: Users,            label: "HR" },
  franchise:   { icon: Building2,        label: "Franchise" },
  partner:     { icon: HeartHandshake,   label: "Partners" },
  quotation:   { icon: FileText,         label: "Finance" },
  invoice:     { icon: Receipt,          label: "Finance" },
};

const QUICK_NAV = [
  { to: "/",                       label: "Go to Dashboard",     icon: LayoutDashboard },
  { to: "/leads",                  label: "Go to Leads",         icon: Target },
  { to: "/contacts",               label: "Go to Contacts",      icon: UserRound },
  { to: "/opportunities",          label: "Go to Opportunities", icon: Sparkles },
  { to: "/tasks",                  label: "Go to Tasks",         icon: ListChecks },
  { to: "/divisions/academy",      label: "Go to Academy",       icon: GraduationCap },
  { to: "/divisions/study-abroad", label: "Go to Study Abroad",  icon: Plane },
  { to: "/divisions/mbbs",         label: "Go to MBBS Abroad",   icon: Plane },
  { to: "/divisions/wealth",       label: "Go to Wealth",        icon: Wallet },
  { to: "/divisions/digital",      label: "Go to Digital",       icon: MonitorSmartphone },
  { to: "/finance/quotations",     label: "Go to Quotations",    icon: FileText },
  { to: "/finance/invoices",       label: "Go to Invoices",      icon: Receipt },
  { to: "/finance/commissions",    label: "Go to Commissions",   icon: IndianRupee },
  { to: "/hr",                     label: "Go to HR",            icon: Users },
  { to: "/franchise",              label: "Go to Franchise",     icon: Building2 },
  { to: "/partners",               label: "Go to Partners",      icon: HeartHandshake },
  { to: "/automations",            label: "Go to Automations",   icon: Zap },
  { to: "/reports",                label: "Go to Reports",       icon: FileText },
];

export default function CommandPalette({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  // Debounced backend search
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length === 0) {
      setResults([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q, limit: 6 }})
        .then(r => setResults(r.data.results || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [query, open]);

  const grouped = useMemo(() => {
    const map = {};
    for (const r of results) {
      (map[r.kind] ||= []).push(r);
    }
    return map;
  }, [results]);

  const go = (route) => {
    onOpenChange(false);
    nav(route);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false} data-testid="command-palette">
      <CommandInput
        data-testid="command-palette-input"
        value={query}
        onValueChange={setQuery}
        placeholder="Search leads, contacts, quotations, invoices, applications… "
      />
      <CommandList className="max-h-[420px]">
        {query.trim().length === 0 && (
          <>
            <CommandGroup heading="Jump to">
              {QUICK_NAV.map(n => {
                const Icon = n.icon;
                return (
                  <CommandItem
                    key={n.to}
                    data-testid={`cmd-quicknav-${n.to}`}
                    onSelect={() => go(n.to)}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 text-[var(--miro-blue-600)]"/>
                    <span>{n.label}</span>
                    <CommandShortcut>{n.to}</CommandShortcut>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator/>
            <div className="px-3 py-3 text-[11px] text-[var(--miro-text-muted)]">
              Type a name, email, invoice number or keyword. Press{" "}
              <kbd className="mx-1 rounded border border-[var(--miro-border)] bg-white px-1.5 py-0.5 text-[10px] font-mono">↑↓</kbd>
              to move,{" "}
              <kbd className="mx-1 rounded border border-[var(--miro-border)] bg-white px-1.5 py-0.5 text-[10px] font-mono">↵</kbd>
              to open.
            </div>
          </>
        )}

        {query.trim().length > 0 && (
          <>
            {loading && (
              <div className="px-3 py-4 text-sm text-[var(--miro-text-muted)]">Searching…</div>
            )}
            {!loading && results.length === 0 && (
              <CommandEmpty>No matches for "{query}".</CommandEmpty>
            )}
            {Object.entries(grouped).map(([kind, rows]) => {
              const meta = KIND_META[kind] || { icon: Sparkles, label: kind };
              const Icon = meta.icon;
              return (
                <CommandGroup key={kind} heading={meta.label}>
                  {rows.map(r => (
                    <CommandItem
                      key={`${kind}-${r.id}`}
                      data-testid={`cmd-result-${kind}-${r.id}`}
                      onSelect={() => go(r.route)}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 text-[var(--miro-blue-600)]"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[var(--miro-navy)] truncate">{r.label}</div>
                        <div className="text-[11px] text-[var(--miro-text-muted)] truncate">{r.sublabel}</div>
                      </div>
                      <CommandShortcut className="text-[10px] uppercase tracking-widest">
                        {kind}
                      </CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
